// M004-F0752 (WebUI #195; design docs/design/M004-F0752-Member-Invoices-Tab.md). The Invoices tag on the member Ledger page: ONE member's
// issued invoices, newest first, with Download PDF. Reuses the M004-F0749 invoice model and PDF helper (checksum-verified). The caller (the
// page) has already gated the tag on billing_transactions_view OR ledger_manage. The request names the member with user_id and NO
// target_user_id; the rows are then filtered again on the client, so another customer's invoice is never rendered (rows dropped are counted).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { invoiceApi } from '../../services/invoiceApi';
import { downloadInvoicePdf } from '../invoices/invoicePdf';
import { errorInfo, formatDay, money, sortNewest } from '../invoices/invoiceModel';
import '../invoices/InvoicesTab.css';

const PAGE = 200;
const MAX_PAGES = 25;

export const MEMBER_STATUS_TEXT = Object.freeze({ issued: 'Issued', part_paid: 'Part paid', paid: 'Paid', void: 'Void' });
export const memberStatusText = (status) => MEMBER_STATUS_TEXT[status] || String(status || 'Unknown');

// Client-side scope (design section 1.4): keep only rows whose user_id is the selected member; report how many were dropped.
export function onlyMember(rows, userId) {
  const id = String(userId);
  const kept = rows.filter((row) => String(row?.user_id) === id);
  return { rows: kept, dropped: rows.length - kept.length };
}

// Pages until next_offset is null (capped), as the org tab does.
export async function loadMemberInvoices(orgId, userId) {
  const rows = [];
  let dropped = 0;
  let offset = 0;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const data = (await invoiceApi.listForMember(orgId, userId, { limit: PAGE, offset }))?.data?.data;
    if (!data || !Array.isArray(data.invoices)) throw new Error('The Ledger answered with an invoice list this page cannot read.');
    const scoped = onlyMember(data.invoices, userId);
    rows.push(...scoped.rows);
    dropped += scoped.dropped;
    if (data.next_offset === null || data.next_offset === undefined) break;
    offset = data.next_offset;
  }
  return { rows: sortNewest(rows), dropped };
}

export default function MemberInvoicesPanel({ orgId, userId, customerName }) {
  const [state, setState] = useState({ status: 'loading', rows: [], dropped: 0, error: null });
  const [pdfError, setPdfError] = useState('');
  const [busyId, setBusyId] = useState('');
  const request = useRef(0);

  const load = useCallback(async () => {
    const mine = request.current + 1;
    request.current = mine;
    setState({ status: 'loading', rows: [], dropped: 0, error: null });
    setPdfError('');
    try {
      const result = await loadMemberInvoices(orgId, userId);
      if (request.current !== mine) return;
      setState({ status: 'ready', rows: result.rows, dropped: result.dropped, error: null });
    } catch (cause) {
      if (request.current !== mine) return;
      const error = errorInfo(cause);
      setState({ status: error.unsupported ? 'unsupported' : 'error', rows: [], dropped: 0, error });
    }
  }, [orgId, userId]);

  useEffect(() => { if (orgId && userId) load(); }, [orgId, userId, load]);

  const download = async (row) => {
    setPdfError('');
    setBusyId(row.invoice_id);
    try { await downloadInvoicePdf(orgId, row); } catch (cause) { const e = errorInfo(cause); setPdfError(`${row.number}: ${e.code ? `${e.code}. ` : ''}${e.message}`); } finally { setBusyId(''); }
  };

  const { status, rows, dropped, error } = state;
  const count = rows.length;
  return (
    <section className="inv-tab member-invoices" aria-labelledby="member-invoices-title" data-testid="member-invoices-panel">
      <header className="inv-header">
        <div>
          <span className="inv-eyebrow">Ledger</span>
          <h3 id="member-invoices-title">Invoices</h3>
          <p className="inv-copy">Invoices issued to {customerName}, newest first. The PDF is the one stored when the invoice was issued.</p>
        </div>
      </header>

      {status === 'loading' ? <p className="inv-state" role="status">Loading invoices…</p> : null}

      {status === 'error' ? (
        <div className="inv-error" role="alert" data-testid="member-invoices-error">
          <strong>The invoices could not be loaded.</strong>
          <span>{error.code ? `${error.code}: ` : ''}{error.message}</span>
          <button type="button" onClick={load}>Retry</button>
        </div>
      ) : null}

      {status === 'unsupported' ? <p className="inv-state" role="status" data-testid="member-invoices-unsupported">Invoices are not available from this Ledger yet.</p> : null}

      {pdfError ? <p className="inv-error" role="alert" data-testid="member-invoice-pdf-error">{pdfError}</p> : null}

      {status === 'ready' && count === 0 ? (
        <p className="inv-state" data-testid="member-invoices-empty">{customerName} has no invoices in this organisation yet.</p>
      ) : null}

      {status === 'ready' && count > 0 ? (
        <>
          <p className="inv-count" role="status" data-testid="member-invoices-status">
            {count} {count === 1 ? 'invoice' : 'invoices'} for {customerName}
            {dropped ? `. ${dropped} ${dropped === 1 ? 'row' : 'rows'} for other customers ${dropped === 1 ? 'was' : 'were'} not shown.` : ''}
          </p>
          <table className="inv-table" data-testid="member-invoices-table">
            <caption className="inv-sr">Invoices for {customerName}, newest first</caption>
            <thead>
              <tr><th scope="col">Number</th><th scope="col">Issued</th><th scope="col" className="inv-num">Total</th><th scope="col">Status</th><th scope="col">Actions</th></tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.invoice_id} data-testid="member-invoice-row">
                  <th scope="row" data-label="Number" data-testid="member-invoice-number">{row.number}</th>
                  <td data-label="Issued">{formatDay(row.issue_date)}</td>
                  <td data-label="Total" className="inv-num">{money(row.total_minor, row.currency)}</td>
                  <td data-label="Status"><span className="inv-pill">{memberStatusText(row.status)}</span></td>
                  <td data-label="Actions" className="inv-actions">
                    <button type="button" disabled={busyId === row.invoice_id} aria-label={`Download PDF for ${row.number}`} onClick={() => download(row)}>
                      {busyId === row.invoice_id ? 'Preparing…' : 'Download PDF'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}
    </section>
  );
}
